
import { useState } from 'react';
import { motion } from 'framer-motion';
import InlineEdit from '../../../components/base/InlineEdit';

const STORAGE_KEY = 'lonetrs_services';

interface Service {
  id: string;
  title: string;
  icon: string;
  description: string;
  features: string[];
}

const defaultServices: Service[] = [
  {
    id: 'residential',
    title: 'Residential Relocation',
    icon: 'ri-home-4-line',
    description: 'Complete household moving services',
    features: ['Packing Services', 'Furniture Assembly', 'Storage Options', 'Insurance Coverage'],
  },
  {
    id: 'commercial',
    title: 'Commercial Interstate',
    icon: 'ri-building-line',
    description: 'Business relocation across state lines',
    features: ['Office Equipment', 'IT Infrastructure', 'Minimal Disruption', 'Weekend Moves'],
  },
  {
    id: 'intrastate',
    title: 'Intrastate Relocations',
    icon: 'ri-map-2-line',
    description: 'Moving services within Nevada',
    features: ['Same-Day Service', 'Local Expertise', 'Flexible Scheduling', 'Competitive Rates'],
  },
  {
    id: 'freight',
    title: 'General Freight',
    icon: 'ri-truck-line',
    description: 'Cargo transportation solutions',
    features: ['LTL & FTL Options', 'Expedited Shipping', 'Tracking System', 'Secure Transport'],
  },
  {
    id: 'building',
    title: 'Building Materials',
    icon: 'ri-stack-line',
    description: 'Construction material transport',
    features: ['Heavy Load Capacity', 'Job Site Delivery', 'Material Protection', 'Scheduled Deliveries'],
  },
  {
    id: 'vehicle',
    title: 'Vehicle Transport',
    icon: 'ri-car-line',
    description: 'Safe vehicle shipping services',
    features: ['Door-to-Door Service', 'Enclosed Transport', 'Insurance Included', 'Multi-Vehicle Discount'],
  },
];

export default function ServicesSection() {
  const [services, setServices] = useState<Service[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultServices;
    } catch { return defaultServices; }
  });
  const [selectedService, setSelectedService] = useState(defaultServices[0].id);
  const [expandedService, setExpandedService] = useState<string | null>(null);

  const save = (updated: Service[]) => {
    setServices(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const updateField = (id: string, field: keyof Service, value: string) => {
    save(services.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const updateFeature = (serviceId: string, featureIndex: number, value: string) => {
    save(services.map(s => {
      if (s.id !== serviceId) return s;
      const features = [...s.features];
      features[featureIndex] = value;
      return { ...s, features };
    }));
  };

  const addFeature = (serviceId: string) => {
    save(services.map(s => {
      if (s.id !== serviceId) return s;
      return { ...s, features: [...s.features, 'New Feature'] };
    }));
  };

  const removeFeature = (serviceId: string, featureIndex: number) => {
    save(services.map(s => {
      if (s.id !== serviceId) return s;
      return { ...s, features: s.features.filter((_, i) => i !== featureIndex) };
    }));
  };

  return (
    <section className="py-24 bg-gradient-to-b from-dark-900 to-dark-800 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(#00D4FF 1px, transparent 1px), linear-gradient(90deg, #00D4FF 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-orbitron text-6xl font-bold text-white mb-4">
            OUR <span className="text-electric-blue">SERVICES</span>
          </h2>
          <p className="font-inter text-white/60 text-lg">
            Comprehensive transportation solutions for every need
          </p>
        </motion.div>

        {/* Service Category Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {services.map((service, index) => (
            <motion.button
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => setSelectedService(service.id)}
              className={`px-6 py-3 rounded-full font-orbitron text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                selectedService === service.id
                  ? 'bg-gradient-to-r from-electric-blue to-electric-cyan text-dark-900 shadow-lg shadow-electric-blue/50'
                  : 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20'
              }`}
            >
              {service.title}
            </motion.button>
          ))}
        </div>

        {/* Service Details Cards */}
        <div className="space-y-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden hover:border-electric-blue/50 transition-all duration-300"
            >
              <button
                onClick={() => setExpandedService(expandedService === service.id ? null : service.id)}
                className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors duration-300"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-electric-blue/20 to-electric-cyan/20 border border-electric-blue/30 flex items-center justify-center">
                    <i className={`${service.icon} text-3xl text-electric-blue w-8 h-8 flex items-center justify-center`}></i>
                  </div>
                  <div className="text-left" onClick={e => e.stopPropagation()}>
                    <h3 className="font-orbitron text-xl font-bold text-white mb-1">
                      <InlineEdit
                        value={service.title}
                        onSave={v => updateField(service.id, 'title', v)}
                        className="text-white"
                        placeholder="Service title..."
                      />
                    </h3>
                    <p className="font-inter text-white/60 text-sm">
                      <InlineEdit
                        value={service.description}
                        onSave={v => updateField(service.id, 'description', v)}
                        className="text-white/60"
                        placeholder="Short description..."
                      />
                    </p>
                  </div>
                </div>
                <i className={`ri-arrow-${expandedService === service.id ? 'up' : 'down'}-s-line text-2xl text-electric-blue transition-transform duration-300 w-6 h-6 flex items-center justify-center`}></i>
              </button>

              {expandedService === service.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-6 pb-6"
                >
                  <div className="pt-4 border-t border-white/10">
                    <h4 className="font-orbitron text-sm text-electric-blue tracking-wider mb-4">
                      KEY FEATURES
                    </h4>
                    <ul className="space-y-3 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center space-x-3">
                          <i className="ri-checkbox-circle-fill text-electric-blue text-lg mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                          <span className="font-inter text-white/80 text-sm flex-1">
                            <InlineEdit
                              value={feature}
                              onSave={v => updateFeature(service.id, idx, v)}
                              className="text-white/80"
                              placeholder="Feature..."
                            />
                          </span>
                          <button
                            onClick={() => removeFeature(service.id, idx)}
                            className="text-red-400/50 hover:text-red-400 text-xs cursor-pointer transition-colors w-4 h-4 flex items-center justify-center"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                    <div className="flex gap-3">
                      <button
                        onClick={() => addFeature(service.id)}
                        className="px-4 py-2 bg-white/10 border border-white/20 text-white/70 font-inter text-xs rounded-full hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-add-line mr-1 w-3 h-3 inline-flex items-center justify-center"></i>
                        Add Feature
                      </button>
                      <button className="px-6 py-2.5 bg-gradient-to-r from-electric-blue to-electric-cyan text-dark-900 font-orbitron font-semibold text-sm rounded-full hover:shadow-lg hover:shadow-electric-blue/50 transition-all duration-300 whitespace-nowrap cursor-pointer">
                        REQUEST QUOTE
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
